/**
 * AgentReview — the Weekly review tab in /crm/agent
 * (docs/crm-agent-weekly-review-plan.md §9).
 *
 * Read-mostly: a history list of weekly reports (stats + AI lessons), and the
 * WR-2 click-to-draft flow — "Send to Tuning" asks the backend to run the
 * tuning copilot against the LIVE prompt version with the recommendation as
 * the instruction; the returned proposal gets an Apply button that writes a
 * DRAFT via the existing tuner route. Publishing stays on the prompt-settings
 * chokepoint. Admin-only — the tab self-hides behind an empty state on 403,
 * same as Tuning.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  CalendarRange, Loader2, RefreshCw, PlayCircle, ShieldAlert, AlertTriangle,
  Lightbulb, SendHorizontal, Check, ExternalLink,
} from 'lucide-react';
import api from '../../../services/api';
import { secondaryBtnCls } from '../../components/filters/styles';
import MarkdownText from '../../components/MarkdownText';
import { Alert } from '../../components/ui';

interface ReportListRow {
  id: string;
  week_start: string;
  partial: boolean;
  status: 'running' | 'complete' | 'failed' | 'budget_skipped_ai';
  spend_usd: number;
  generated_by: string;
  created_at: string;
  completed_at: string | null;
  recommendation_count: number;
}

interface Recommendation {
  target_kind: string;
  target_key: string;
  evidence: string;
  proposed_edit_intent: string;
  drafted_at?: string | null;
}

interface ReportDetail extends Omit<ReportListRow, 'recommendation_count'> {
  stats: any;
  lessons_md: string | null;
  recommendations: Recommendation[];
  playbook_version: number | null;
  error: string | null;
}

interface DraftResult {
  reply?: string;
  proposals?: { id: string; proposal: any }[];
}

const STATUS_META: Record<ReportListRow['status'], { label: string; cls: string }> = {
  running: { label: 'Running', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200' },
  complete: { label: 'Complete', cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' },
  failed: { label: 'Failed', cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200' },
  budget_skipped_ai: { label: 'Stats only (budget)', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' },
};

function Stat({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{value}</div>
      {hint ? <div className="text-[11px] text-gray-500 dark:text-gray-400">{hint}</div> : null}
    </div>
  );
}

const fmtLat = (l: { p50: number | null; p90: number | null; n: number } | undefined) =>
  !l || l.n === 0 || l.p50 == null ? '—' : `${l.p50}m / ${l.p90}m`;

export default function AgentReview() {
  const [reports, setReports] = useState<ReportListRow[]>([]);
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ReportDetail | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [queued, setQueued] = useState(false);
  const [drafting, setDrafting] = useState<number | null>(null);
  const [draftResults, setDraftResults] = useState<Record<number, DraftResult>>({});
  const [applied, setApplied] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/crm/agent/review/reports');
      setReports(res.data.reports || []);
      setEnabled(res.data.enabled !== false);
      setForbidden(false);
    } catch (e: any) {
      if (e?.response?.status === 403) setForbidden(true);
      else setError(e?.response?.data?.error || 'Failed to load weekly reviews');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    let gone = false;
    setDetail(null);
    setDraftResults({});
    api.get(`/api/crm/agent/review/reports/${selectedId}`)
      .then((res) => { if (!gone) setDetail(res.data.report); })
      .catch((e) => { if (!gone) setError(e?.response?.data?.error || 'Failed to load the report'); });
    return () => { gone = true; };
  }, [selectedId]);

  const runNow = async () => {
    setError(null);
    try {
      await api.post('/api/crm/agent/review/run', { partial: true });
      setQueued(true);
    } catch (e: any) {
      setError(e?.response?.status === 409
        ? 'The weekly review is not enabled yet (AGENT_REVIEW_ENABLED).'
        : e?.response?.data?.error || 'Failed to queue the run');
    }
  };

  const sendToTuning = async (idx: number) => {
    if (!detail) return;
    setDrafting(idx);
    setError(null);
    try {
      const res = await api.post(`/api/crm/agent/review/reports/${detail.id}/recommendations/${idx}/draft`, {});
      setDraftResults((prev) => ({ ...prev, [idx]: res.data }));
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Drafting failed');
    } finally {
      setDrafting(null);
    }
  };

  const applyProposal = async (idx: number, pid: string, proposal: any) => {
    setError(null);
    try {
      await api.post('/api/crm/agent/tuner/apply', { proposal });
      setApplied((prev) => ({ ...prev, [`${idx}:${pid}`]: true }));
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Apply failed');
    }
  };

  if (forbidden) {
    return (
      <div className="mt-3 rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
        <ShieldAlert className="mx-auto mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
        <p className="text-lg font-medium text-gray-900 dark:text-gray-100">Admin only</p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
          Weekly reviews summarize conversations and propose prompt changes, so they sit behind the CRM admin gate.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3 grid gap-3 lg:grid-cols-[280px_1fr]">
      {/* -------- history list -------- */}
      <div>
        <div className="mb-2 flex items-center gap-2">
          <button type="button" className={secondaryBtnCls} onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
          <button type="button" className={secondaryBtnCls} onClick={runNow} disabled={!enabled}>
            <PlayCircle className="h-4 w-4" />
            Run now
          </button>
        </div>
        {queued && (
          <Alert tone="info" size="sm" icon={false} className="mb-2">
            Queued — the worker builds the current week's partial report; refresh in a couple of minutes.
          </Alert>
        )}
        {!enabled && (
          <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            The weekly review is <strong>off</strong> (AGENT_REVIEW_ENABLED is unset) — past reports stay readable.
          </div>
        )}
        {reports.length === 0 && !loading ? (
          <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center dark:border-gray-600">
            <CalendarRange className="mx-auto mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No weekly reviews yet. The first one lands Monday morning — or press Run now.
            </p>
          </div>
        ) : (
          <ul className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            {reports.map((r) => {
              const meta = STATUS_META[r.status];
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(r.id)}
                    className={`flex w-full items-center justify-between gap-2 border-b border-gray-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/40 ${selectedId === r.id ? 'bg-primary-50 dark:bg-primary-900/20' : ''}`}
                  >
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      Week of {r.week_start}{r.partial ? ' (partial)' : ''}
                    </span>
                    <span className="flex items-center gap-2">
                      {r.recommendation_count > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <Lightbulb className="h-3.5 w-3.5" />{r.recommendation_count}
                        </span>
                      )}
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.cls}`}>{meta.label}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* -------- detail -------- */}
      <div>
        {error && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
            <AlertTriangle className="h-4 w-4 shrink-0" />{error}
          </div>
        )}
        {!detail ? (
          <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
            Pick a week on the left.
          </div>
        ) : (
          <div className="space-y-4">
            {detail.status === 'failed' && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
                This run failed: {detail.error || 'unknown error'} — Run now to retry.
              </div>
            )}
            {/* stat tiles */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              <Stat label="SMS this week" value={detail.stats?.volume?.sms_total ?? '—'} hint={`${detail.stats?.volume?.threads_touched ?? 0} threads`} />
              <Stat label="Human reply (p50/p90)" value={fmtLat(detail.stats?.responsiveness?.human_reply_latency_min)} hint={`${detail.stats?.responsiveness?.human_reply_latency_min?.n ?? 0} replies`} />
              <Stat label="Agent reply (p50/p90)" value={fmtLat(detail.stats?.responsiveness?.agent_reply_latency_min)} hint={`${detail.stats?.responsiveness?.agent_reply_latency_min?.n ?? 0} replies`} />
              <Stat
                label="Copilot edit rate"
                value={detail.stats?.agent?.copilot?.edit_rate == null ? '—' : `${Math.round(detail.stats.agent.copilot.edit_rate * 100)}%`}
                hint={`${detail.stats?.agent?.copilot?.reviewed ?? 0} reviewed`}
              />
              <Stat label="Escalations" value={Object.values(detail.stats?.agent?.escalations_by_kind || {}).reduce((a: number, b: any) => a + Number(b || 0), 0)} hint={`${detail.stats?.agent?.escalations_unresolved ?? 0} unresolved`} />
              <Stat label="Agent spend" value={`$${Number(detail.stats?.agent?.spend_usd ?? 0).toFixed(2)}`} hint={`report cost $${Number(detail.spend_usd ?? 0).toFixed(2)}`} />
              <Stat label="Prompt publishes" value={(detail.stats?.prompt_churn || []).length} hint="during this week" />
            </div>

            {/* lessons narrative */}
            <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
              <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Lessons learned</h3>
              {detail.lessons_md
                ? <MarkdownText text={detail.lessons_md} />
                : <p className="text-sm text-gray-500 dark:text-gray-400">No AI narrative for this run{detail.status === 'budget_skipped_ai' ? ' (budget cap hit — the numbers above still cover the week)' : ''}.</p>}
            </div>

            {/* recommendations */}
            {detail.recommendations.length > 0 && (
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-gray-100">Recommended adjustments</h3>
                <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                  Send to Tuning drafts the edit fresh against the LIVE version — nothing publishes without you.
                </p>
                <ul className="space-y-3">
                  {detail.recommendations.map((rec, idx) => (
                    <li key={idx} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {rec.target_kind}: <span className="font-mono text-xs">{rec.target_key}</span>
                          </div>
                          <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{rec.proposed_edit_intent}</p>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Evidence: {rec.evidence}</p>
                        </div>
                        <button
                          type="button"
                          className={secondaryBtnCls}
                          disabled={drafting === idx}
                          onClick={() => sendToTuning(idx)}
                        >
                          {drafting === idx ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" />}
                          Send to Tuning
                        </button>
                      </div>
                      {draftResults[idx] && (
                        <div className="mt-3 rounded-lg bg-gray-50 p-3 text-sm dark:bg-gray-900/40">
                          {draftResults[idx].reply && <MarkdownText text={draftResults[idx].reply!} />}
                          {(draftResults[idx].proposals || []).map(({ id: pid, proposal }) => (
                            <div key={pid} className="mt-2 flex items-center justify-between gap-2 rounded border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
                              <span className="text-xs text-gray-700 dark:text-gray-300">
                                {proposal.kind === 'prompt' ? `Draft for ${proposal.key}` : proposal.kind === 'brief' ? 'Study brief draft' : 'Settings change'}
                                {proposal.rationale ? ` — ${proposal.rationale}` : ''}
                              </span>
                              {applied[`${idx}:${pid}`] ? (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-300">
                                  <Check className="h-3.5 w-3.5" /> Draft saved — review &amp; publish in Settings
                                  {proposal.kind === 'prompt' && proposal.key && (
                                    <a href={`/crm/settings/prompts/${proposal.key}`} className="ml-1 inline-flex items-center gap-0.5 underline">
                                      open <ExternalLink className="h-3 w-3" />
                                    </a>
                                  )}
                                </span>
                              ) : (
                                <button type="button" className={secondaryBtnCls} onClick={() => applyProposal(idx, pid, proposal)}>
                                  Apply draft
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
